import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";
import type {
  bulkPaystubResponse,
  bulkPaystubWorker,
  bulkPaystubSummary,
} from "types/api-types/payroll/everee-paystub-bulk/everee-paystub-bulk.types";

/** Extended response type — includes ZIP download fields from backend */
type BulkPaystubResponseWithZip = bulkPaystubResponse & {
  zipDownloadUrl?: string;
  zipBase64?: string;
  zipFilename?: string;
};

/**
 * Get worker ID - handles both workerId and id fields from API
 */
const getWorkerId = (worker: evereeWorkerData): string => {
  return worker.workerId || (worker as unknown as { id: string }).id || "";
};

/**
 * BulkPaystubStore - Manages bulk paystub downloading for admin interface
 */
class BulkPaystubStore {
  rootStore: RootStore;

  // Observable state
  isLoading = false;
  isDownloading = false;
  error: string | null = null;
  selectedWorkerIds: string[] = [];
  startDate = "";
  endDate = "";
  searchTerm = "";
  bulkResponse: bulkPaystubResponse | null = null;
  downloadProgress = 0;
  totalDownloads = 0;

  // Socket progress tracking
  progressPhase: string | null = null;
  progressPercent = 0;
  progressMessage: string | null = null;
  socketProgressHandler: ((data: unknown) => void) | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false, socketProgressHandler: false });
  }

  // ServiceStore accessors
  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  get evereePaystubBulkStore(): ServiceStore {
    return this.rootStore.evereePaystubBulkStore as ServiceStore;
  }

  // Computed: current company code from queryParams or appLayoutStore
  get currentCompanyCode(): string {
    const queryCompanyId = this.rootStore.routerStore?.queryParams?.companyId as string;
    if (queryCompanyId) {
      return queryCompanyId;
    }
    return this.rootStore.appLayoutStore?.selectedCompanyId || "";
  }

  // Computed: workers from evereeWorkerDataStore
  get workers(): evereeWorkerData[] {
    return (this.evereeWorkerDataStore?.entities || []) as unknown as evereeWorkerData[];
  }

  // Computed: filtered workers by search term
  get filteredWorkers(): evereeWorkerData[] {
    if (!this.searchTerm) {
      return this.workers;
    }

    const term = this.searchTerm.toLowerCase();
    return this.workers.filter(
      (w) =>
        w.firstName?.toLowerCase().includes(term) ||
        w.lastName?.toLowerCase().includes(term) ||
        w.email?.toLowerCase().includes(term) ||
        getWorkerId(w).toLowerCase().includes(term)
    );
  }

  // Computed: selected workers
  get selectedWorkers(): evereeWorkerData[] {
    return this.workers.filter((w) => this.selectedWorkerIds.includes(getWorkerId(w)));
  }

  // Computed: has any selection
  get hasSelection(): boolean {
    return this.selectedWorkerIds.length > 0;
  }

  // Computed: summary from bulk response
  get summary(): bulkPaystubSummary | null {
    return this.bulkResponse?.summary || null;
  }

  // Computed: worker paystub data from response
  get workerPaystubData(): bulkPaystubWorker[] {
    return this.bulkResponse?.workers || [];
  }

  // Action: toggle worker selection
  toggleWorkerSelection(workerId: string): void {
    const index = this.selectedWorkerIds.indexOf(workerId);
    if (index === -1) {
      // Add worker - use array spread for reliable reactivity
      this.selectedWorkerIds = [...this.selectedWorkerIds, workerId];
    } else {
      // Remove worker - use filter for reliable reactivity
      this.selectedWorkerIds = this.selectedWorkerIds.filter((id) => id !== workerId);
    }
  }

  // Action: select all workers
  selectAllWorkers(): void {
    this.selectedWorkerIds = this.filteredWorkers.map((w) => getWorkerId(w));
  }

  // Action: clear selection
  clearSelection(): void {
    this.selectedWorkerIds = [];
  }

  // Action: set search term
  setSearchTerm(term: string): void {
    this.searchTerm = term;
  }

  // Action: set start date
  setStartDate(date: string): void {
    this.startDate = date;
  }

  // Action: set end date
  setEndDate(date: string): void {
    this.endDate = date;
  }

  // Action: start listening for socket progress events
  startProgressListener(): void {
    this.stopProgressListener();
    const socket = this.rootStore.app?.io;
    if (!socket) return;

    const handler = (data: unknown) => {
      const event = data as Record<string, unknown>;
      if (event?.type !== "paystub-bulk-progress") return;

      runInAction(() => {
        this.progressPhase = (event.phase as string) || null;
        this.progressPercent = (event.percent as number) || 0;
        this.progressMessage = (event.message as string) || null;
      });
    };

    this.socketProgressHandler = handler;
    socket.on("message", handler);
  }

  // Action: stop listening for socket progress events
  stopProgressListener(): void {
    if (this.socketProgressHandler) {
      const socket = this.rootStore.app?.io;
      if (socket) {
        socket.off("message", this.socketProgressHandler);
      }
      this.socketProgressHandler = null;
    }
    this.progressPhase = null;
    this.progressPercent = 0;
    this.progressMessage = null;
  }

  // Async Action: load workers
  async loadWorkers(companyId?: string): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const query: Record<string, unknown> = { page: 0, size: 100 };
      if (companyId) {
        query.companyId = companyId;
      }

      await this.evereeWorkerDataStore.find({ query });
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to load workers";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  // Async Action: fetch bulk paystubs
  async fetchBulkPaystubs(
    includeDownloadLinks = false,
    asZip = false,
  ): Promise<BulkPaystubResponseWithZip | null> {
    if (this.selectedWorkerIds.length === 0) {
      this.error = "No workers selected";
      return null;
    }

    const companyId = this.currentCompanyCode;
    if (!companyId) {
      this.error = "No company selected. Please select a company from the dropdown.";
      return null;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Payload follows API schema - companyId goes in query params, not body
      const payload: Record<string, unknown> = {
        workerIds: this.selectedWorkerIds,
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined,
        includeDownloadLinks,
      };

      if (asZip) {
        payload.asZip = true;
      }

      // Pass companyId in query params for Everee service hooks
      const response = await this.evereePaystubBulkStore.create(payload, {
        query: { companyId },
      });

      const typed = response as unknown as BulkPaystubResponseWithZip;

      runInAction(() => {
        this.bulkResponse = typed;
      });

      return typed;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to fetch paystubs";
      });
      return null;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  // Async Action: download all paystubs
  async downloadAllPaystubs(): Promise<void> {
    if (this.selectedWorkerIds.length === 0) {
      this.error = "No workers selected";
      return;
    }

    this.isDownloading = true;
    this.downloadProgress = 0;
    this.totalDownloads = 1;
    this.error = null;
    this.startProgressListener();

    try {
      // Request ZIP bundle when downloading for multiple workers
      const useZip = this.selectedWorkerIds.length > 1;
      const response = await this.fetchBulkPaystubs(true, useZip);

      if (!response) {
        return;
      }

      // Use base64 ZIP if available (inline in response)
      if (response.zipBase64) {
        runInAction(() => { this.totalDownloads = 1; });
        this.triggerBase64Download(
          response.zipBase64,
          response.zipFilename || "paystubs.zip"
        );
        runInAction(() => { this.downloadProgress = 1; });
        return;
      }

      // Fallback: ZIP download URL
      if (response.zipDownloadUrl) {
        runInAction(() => { this.totalDownloads = 1; });
        await this.triggerDownload(response.zipDownloadUrl, "paystubs.zip");
        runInAction(() => { this.downloadProgress = 1; });
        return;
      }

      // Fallback: download individual PDFs
      const allLinks = response.workers.flatMap((w) => w.downloadLinks || []);
      runInAction(() => { this.totalDownloads = allLinks.length; });

      if (allLinks.length === 0) {
        runInAction(() => {
          this.error = "No paystubs available for download";
          this.isDownloading = false;
        });
        return;
      }

      for (let i = 0; i < allLinks.length; i++) {
        const link = allLinks[i];
        await this.triggerDownload(link.url, `paystub-${link.paymentId}.pdf`);

        runInAction(() => {
          this.downloadProgress = i + 1;
        });

        if (i < allLinks.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to download paystubs";
      });
    } finally {
      runInAction(() => {
        this.clearDownloadStateAfterDelay();
      });
    }
  }

  // Async Action: download paystubs for a single worker
  async downloadWorkerPaystubs(workerId: string): Promise<void> {
    const companyId = this.currentCompanyCode;
    if (!companyId) {
      this.error = "No company selected. Please select a company from the dropdown.";
      return;
    }

    this.isDownloading = true;
    this.error = null;

    try {
      // Payload follows API schema - companyId goes in query params, not body
      const payload = {
        workerIds: [workerId],
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined,
        includeDownloadLinks: true,
      };

      // Pass companyId in query params for Everee service hooks
      const response = await this.evereePaystubBulkStore.create(payload, {
        query: { companyId },
      }) as unknown as bulkPaystubResponse;

      const workerData = response.workers.find((w) => w.workerId === workerId);
      const links = workerData?.downloadLinks || [];

      if (links.length === 0) {
        runInAction(() => {
          this.error = "No paystubs available for this worker";
        });
        return;
      }

      // Download each PDF
      for (const link of links) {
        await this.triggerDownload(link.url, `paystub-${link.paymentId}.pdf`);
        await new Promise((resolve) => setTimeout(resolve, 300));
      }
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to download paystubs";
      });
    } finally {
      runInAction(() => {
        this.isDownloading = false;
      });
    }
  }

  // Async Action: fetch ALL paystubs for single worker (no date filter)
  async fetchSingleWorkerPaystubs(): Promise<bulkPaystubResponse | null> {
    if (this.selectedWorkerIds.length !== 1) {
      this.error = "Single worker paystubs requires exactly one worker selected";
      return null;
    }

    const companyId = this.currentCompanyCode;
    if (!companyId) {
      this.error = "No company selected. Please select a company from the dropdown.";
      return null;
    }

    this.isLoading = true;
    this.error = null;

    try {
      // Fetch without date filter to get all paystubs
      const payload = {
        workerIds: this.selectedWorkerIds,
        includeDownloadLinks: false,
      };

      const response = await this.evereePaystubBulkStore.create(payload, {
        query: { companyId },
      });

      runInAction(() => {
        this.bulkResponse = response as unknown as bulkPaystubResponse;
      });

      return response as unknown as bulkPaystubResponse;
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to fetch paystubs";
      });
      return null;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  // Async Action: download single paystub by paymentId
  async downloadSinglePaystub(workerId: string, paymentId: string | number): Promise<void> {
    const companyId = this.currentCompanyCode;
    if (!companyId) {
      this.error = "No company selected. Please select a company from the dropdown.";
      return;
    }

    this.isDownloading = true;
    this.error = null;

    try {
      const payload = {
        workerIds: [workerId],
        includeDownloadLinks: true,
      };

      const response = await this.evereePaystubBulkStore.create(payload, {
        query: { companyId },
      }) as unknown as bulkPaystubResponse;

      const workerData = response.workers.find((w) => w.workerId === workerId);
      const link = workerData?.downloadLinks?.find((l) => l.paymentId === paymentId);

      if (!link) {
        runInAction(() => {
          this.error = "No download link available for this paystub";
        });
        return;
      }

      await this.triggerDownload(link.url, `paystub-${paymentId}.pdf`);
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to download paystub";
      });
    } finally {
      runInAction(() => {
        this.isDownloading = false;
      });
    }
  }

  // Async Action: download filtered paystubs (for single worker dialog)
  async downloadFilteredPaystubs(workerId: string, paymentIds: (string | number)[]): Promise<void> {
    const companyId = this.currentCompanyCode;
    if (!companyId) {
      this.error = "No company selected. Please select a company from the dropdown.";
      return;
    }

    if (paymentIds.length === 0) {
      this.error = "No paystubs to download";
      return;
    }

    this.isDownloading = true;
    this.downloadProgress = 0;
    this.totalDownloads = paymentIds.length;
    this.error = null;

    try {
      const payload = {
        workerIds: [workerId],
        includeDownloadLinks: true,
      };

      const response = await this.evereePaystubBulkStore.create(payload, {
        query: { companyId },
      }) as unknown as bulkPaystubResponse;

      const workerData = response.workers.find((w) => w.workerId === workerId);
      const links = (workerData?.downloadLinks || []).filter((l) =>
        paymentIds.includes(l.paymentId)
      );

      if (links.length === 0) {
        runInAction(() => {
          this.error = "No download links available for the selected paystubs";
        });
        return;
      }

      for (let i = 0; i < links.length; i++) {
        const link = links[i];
        await this.triggerDownload(link.url, `paystub-${link.paymentId}.pdf`);

        runInAction(() => {
          this.downloadProgress = i + 1;
        });

        if (i < links.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to download paystubs";
      });
    } finally {
      runInAction(() => {
        this.isDownloading = false;
        this.downloadProgress = 0;
        this.totalDownloads = 0;
      });
    }
  }

  // Async Action: download paystubs for specific pay period (for bulk dialog)
  async downloadPaystubsForPeriod(periodStart: string, periodEnd: string): Promise<void> {
    if (this.selectedWorkerIds.length === 0) {
      this.error = "No workers selected";
      return;
    }

    const companyId = this.currentCompanyCode;
    if (!companyId) {
      this.error = "No company selected. Please select a company from the dropdown.";
      return;
    }

    this.isDownloading = true;
    this.downloadProgress = 0;
    this.totalDownloads = 1;
    this.error = null;
    this.startProgressListener();

    try {
      // Request ZIP bundle when downloading for multiple workers
      const useZip = this.selectedWorkerIds.length > 1;
      const payload: Record<string, unknown> = {
        workerIds: this.selectedWorkerIds,
        includeDownloadLinks: true,
      };

      if (useZip) {
        payload.asZip = true;
      }

      const response = await this.evereePaystubBulkStore.create(payload, {
        query: { companyId },
      }) as unknown as BulkPaystubResponseWithZip;

      // Use base64 ZIP if available (inline in response)
      if (response.zipBase64) {
        runInAction(() => { this.totalDownloads = 1; });
        const safePeriod = `${periodStart}_${periodEnd}`.replace(/\//g, "-");
        this.triggerBase64Download(
          response.zipBase64,
          response.zipFilename || `paystubs-${safePeriod}.zip`
        );
        runInAction(() => { this.downloadProgress = 1; });
        return;
      }

      // Fallback: ZIP download URL
      if (response.zipDownloadUrl) {
        runInAction(() => { this.totalDownloads = 1; });
        const safePeriod = `${periodStart}_${periodEnd}`.replace(/\//g, "-");
        await this.triggerDownload(response.zipDownloadUrl, `paystubs-${safePeriod}.zip`);
        runInAction(() => { this.downloadProgress = 1; });
        return;
      }

      // Fallback: filter and download individual PDFs for the matching pay period
      const linksToDownload: { url: string; paymentId: string | number; workerName: string }[] = [];

      response.workers.forEach((workerData) => {
        const matchingPaystub = workerData.paystubs.find((p) => {
          const pStart = p.payPeriodStart || p.payDate;
          const pEnd = p.payPeriodEnd || p.payDate;
          return pStart === periodStart && pEnd === periodEnd;
        });

        if (matchingPaystub) {
          const link = workerData.downloadLinks?.find(
            (l) => l.paymentId === matchingPaystub.paymentId
          );
          if (link) {
            linksToDownload.push({
              url: link.url,
              paymentId: link.paymentId,
              workerName: workerData.workerName,
            });
          }
        }
      });

      runInAction(() => { this.totalDownloads = linksToDownload.length; });

      if (linksToDownload.length === 0) {
        runInAction(() => {
          this.error = "No paystubs available for download for the selected period";
        });
        return;
      }

      for (let i = 0; i < linksToDownload.length; i++) {
        const item = linksToDownload[i];
        const safeWorkerName = item.workerName.replace(/[^a-zA-Z0-9]/g, "-");
        await this.triggerDownload(item.url, `paystub-${safeWorkerName}-${item.paymentId}.pdf`);

        runInAction(() => {
          this.downloadProgress = i + 1;
        });

        if (i < linksToDownload.length - 1) {
          await new Promise((resolve) => setTimeout(resolve, 300));
        }
      }
    } catch (error) {
      runInAction(() => {
        this.error = error instanceof Error ? error.message : "Failed to download paystubs";
      });
    } finally {
      runInAction(() => {
        this.clearDownloadStateAfterDelay();
      });
    }
  }

  // Helper: clear download state after a delay so the user sees the final count
  private clearDownloadStateAfterDelay(): void {
    this.isDownloading = false;
    setTimeout(() => {
      runInAction(() => {
        this.downloadProgress = 0;
        this.totalDownloads = 0;
        this.stopProgressListener();
      });
    }, 30_000);
  }

  // Helper: trigger file download from URL
  private async triggerDownload(url: string, filename: string): Promise<void> {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.target = "_blank";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  // Helper: trigger file download from base64 string
  private triggerBase64Download(base64: string, filename: string, mimeType = "application/zip"): void {
    const byteString = atob(base64);
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      bytes[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  // Action: reset store state
  reset(): void {
    this.stopProgressListener();
    this.isLoading = false;
    this.isDownloading = false;
    this.error = null;
    this.selectedWorkerIds = [];
    this.startDate = "";
    this.endDate = "";
    this.searchTerm = "";
    this.bulkResponse = null;
    this.downloadProgress = 0;
    this.totalDownloads = 0;
  }
}

export default BulkPaystubStore;
